import { useSyncExternalStore } from "react";

export type IPageMetaLegacyButtonColor =
  | "primary"
  | "accent"
  | "error"
  | "neutral"
  | "delete"
  | "excel";

export type IPageMetaAction = {
  label: string;
  to?: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  openInNewTab?: boolean;
  variant?: "solid" | "outline";
  /** Red primary (default); `secondary` = staff blue filled title action. */
  tone?: "primary" | "secondary";
  /** When set, member title bar renders this action with LegacyButton instead of default link styles. */
  legacyButtonColor?: IPageMetaLegacyButtonColor;
  legacyButtonStyle?: "filled" | "ghost" | "outlined";
} | null;

export interface IBreadcrumbAncestor {
  label: string;
  href: string;
}

export interface IDynamicPageMeta {
  title?: string | null;
  subtitle?: string | null;
  breadcrumbLabels?: Record<string, string>;
  breadcrumbAncestors?: IBreadcrumbAncestor[];
  titleAction?: IPageMetaAction;
  titleActionSecondary?: IPageMetaAction;
  breadcrumbAction?: IPageMetaAction;
}

interface IPageMetaState {
  byPath: Record<string, IDynamicPageMeta>;
}

let state: IPageMetaState = {
  byPath: {},
};

const listeners = new Set<() => void>();

const emitChange = () => {
  listeners.forEach((listener) => listener());
};

const normalizePath = (path: string): string => {
  if (!path) {
    return "/";
  }

  const lowerPath = path.toLowerCase();
  return lowerPath.startsWith("/") ? lowerPath : `/${lowerPath}`;
};

const normalizeBreadcrumbLabels = (
  labels?: Record<string, string>
): Record<string, string> | undefined => {
  if (!labels) {
    return undefined;
  }

  return Object.entries(labels).reduce<Record<string, string>>(
    (accumulator, [path, label]) => {
      accumulator[normalizePath(path)] = label;
      return accumulator;
    },
    {}
  );
};

export const setRoutePageMeta = (
  path: string,
  meta: IDynamicPageMeta
): void => {
  const normalizedPath = normalizePath(path);
  const normalizedMeta: IDynamicPageMeta = {
    ...meta,
    breadcrumbLabels: normalizeBreadcrumbLabels(meta.breadcrumbLabels),
  };

  state = {
    byPath: {
      ...state.byPath,
      [normalizedPath]: {
        ...state.byPath[normalizedPath],
        ...normalizedMeta,
      },
    },
  };

  emitChange();
};

export const clearRoutePageMeta = (path: string): void => {
  const normalizedPath = normalizePath(path);
  if (!state.byPath[normalizedPath]) {
    return;
  }

  const updatedByPath = { ...state.byPath };
  delete updatedByPath[normalizedPath];

  state = {
    byPath: updatedByPath,
  };

  emitChange();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSnapshot = () => state;

export const useRoutePageMetaSnapshot = (
  path: string
): IDynamicPageMeta | undefined => {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return snapshot.byPath[normalizePath(path)];
};
